// Phone-book import. Uses the native Capacitor Contacts plugin inside the APK,
// and falls back to the browser Contact Picker API (Chrome on Android, HTTPS)
// on the web. Returns { name, mobile } for a single picked contact, or null.
import { Capacitor } from '@capacitor/core';

const tenDigit = (s) => {
  const d = String(s || '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
};
// Indian mobile = 10 digits starting 6-9. Landlines / short codes are not invitable.
const isMobile = (d) => /^[6-9]\d{9}$/.test(d);
// A contact can hold several numbers (home, work, 2 SIMs): prefer the first real
// mobile, else the first number at all.
const bestMobile = (phones) => {
  const nums = (phones || []).map(p => tenDigit(p && p.number)).filter(Boolean);
  return nums.find(isMobile) || nums[0] || '';
};
// Every distinct number of one contact, with its label ("Mobile", "Work", …)
// so the app can ASK which one to invite - a family contact often holds a
// dozen numbers and guessing the first one is usually wrong.
const allNumbers = (phones) => {
  const out = [];
  const seen = new Set();
  for (const p of phones || []) {
    const num = tenDigit(p && p.number);
    if (!num || seen.has(num)) continue;
    seen.add(num);
    out.push({ number: num, label: (p && (p.label || p.type)) || '', mobile: isMobile(num) });
  }
  // Real mobiles first: those are the ones an invite can go to.
  return out.sort((a, b) => Number(b.mobile) - Number(a.mobile));
};

// A phone book usually holds one display name, "Arjandas Punjabi". Indian
// usage puts the family name last, so the LAST word is the surname and the rest
// is the name - that saves re-typing it into two fields. A structured family
// name from the contact always wins over this guess.
export const splitDisplayName = (display, family) => {
  const fam = String(family || '').trim();
  const words = String(display || '').trim().split(/\s+/).filter(Boolean);
  if (fam) {
    // The display name usually repeats the surname - drop it from the name.
    const rest = words.filter(w => w.toLowerCase() !== fam.toLowerCase());
    return { name: (rest.length ? rest : words).join(' '), surname: fam };
  }
  if (words.length < 2) return { name: words.join(' '), surname: '' };
  return { name: words.slice(0, -1).join(' '), surname: words[words.length - 1] };
};

// Is a single-contact picker available? (used to show/hide the button)
export const contactsSupported = () =>
  Capacitor.isNativePlatform() ||
  (typeof navigator !== 'undefined' && !!navigator.contacts && !!navigator.contacts.select);

// Bulk import (read the whole phone book) is native-only — the web Contact
// Picker can't enumerate all contacts.
export const bulkContactsSupported = () => Capacitor.isNativePlatform();

// Read the entire phone book → [{ surname, name, fatherHusband, mobile, display }],
// deduped by mobile, sorted by display name. Native APK only.
export async function getDeviceContacts() {
  if (!Capacitor.isNativePlatform()) throw new Error('Bulk contact import works in the mobile app');
  const { Contacts } = await import('@capacitor-community/contacts');
  const perm = await Contacts.requestPermissions();
  if (perm.contacts !== 'granted') throw new Error('Contacts permission denied');
  const res = await Contacts.getContacts({ projection: { name: true, phones: true } });
  const list = (res && res.contacts) || [];
  const out = [];
  const seen = new Set();
  for (const c of list) {
    const nm = c.name || {};
    const mobile = bestMobile(c.phones);
    const name = nm.given || nm.display || '';
    const surname = nm.family || '';
    const display = [surname, name].filter(Boolean).join(' ') || nm.display || '(no name)';
    if (!mobile && display === '(no name)') continue;
    const key = mobile || `n:${display}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ surname, name, fatherHusband: nm.middle || '', mobile, display, key });
  }
  out.sort((a, b) => a.display.localeCompare(b.display));
  return out;
}

// Returns { surname, name, fatherHusband, mobile } for one picked contact.
export async function pickContactFromDevice() {
  // ── Native APK: system contact picker via @capacitor-community/contacts ──
  if (Capacitor.isNativePlatform()) {
    const { Contacts } = await import('@capacitor-community/contacts');
    const perm = await Contacts.requestPermissions();
    if (perm.contacts !== 'granted') throw new Error('Contacts permission denied');
    const res = await Contacts.pickContact({ projection: { name: true, phones: true } });
    const contact = res && res.contact;
    if (!contact) return null;
    const nm = contact.name || {};
    // given → Name, family → Surname, middle → Father/Husband (best effort).
    // Most contacts have only a display name, so split it: last word = surname.
    const split = splitDisplayName(nm.display || [nm.given, nm.family].filter(Boolean).join(' '), nm.family);
    const name = nm.given || split.name || '';
    return {
      surname: nm.family || split.surname || '',
      name,
      fatherHusband: nm.middle || '',
      mobile: bestMobile(contact.phones),
      numbers: allNumbers(contact.phones),
    };
  }

  // ── Web: Contact Picker API (Chrome/Android over HTTPS, needs a user gesture) ──
  if (typeof navigator !== 'undefined' && navigator.contacts && navigator.contacts.select) {
    const picked = await navigator.contacts.select(['name', 'tel'], { multiple: false });
    if (!picked || !picked.length) return null;
    const c = picked[0];
    // The web API only gives a display name — split it the same way.
    const webNums = allNumbers((c.tel || []).map(number => ({ number })));
    const webSplit = splitDisplayName((c.name && c.name[0]) || '', '');
    return {
      surname: webSplit.surname,
      name: webSplit.name,
      fatherHusband: '',
      mobile: tenDigit(c.tel && c.tel[0]),
      numbers: webNums,
    };
  }

  throw new Error('Contact picker is not supported on this device');
}
